import { getVariant } from '@chessbut/engine';
import { useEffect } from 'react';
import { AuthModal } from './components/AuthModal.tsx';
import { ConnectionBanner, RoomPanel, SearchingBar, ShareFallback } from './components/Overlays.tsx';
import { SettingsModal } from './components/SettingsModal.tsx';
import { BottomNav, Footer, Header } from './components/Shell.tsx';
import { Toasts, toast } from './components/Toast.tsx';
import { navigate, useLocation } from './lib/router.tsx';
import { useSession } from './lib/session.ts';
import { sounds } from './lib/sound.ts';
import { track } from './lib/track.ts';
import { closeAuth, closeSettings, useUi } from './lib/ui.ts';
import { client, useOnline } from './online/client.ts';
import { AdminPage } from './screens/AdminPage.tsx';
import { ChallengeScreen } from './screens/ChallengeScreen.tsx';
import { DaysPage } from './screens/DaysPage.tsx';
import { Home } from './screens/Home.tsx';
import { IdeasPage } from './screens/IdeasPage.tsx';
import { JoinPage } from './screens/JoinPage.tsx';
import { LeaderboardPage } from './screens/LeaderboardPage.tsx';
import { LocalGameScreen } from './screens/LocalGameScreen.tsx';
import { OnlineGameScreen } from './screens/OnlineGameScreen.tsx';
import { ProfilePage } from './screens/ProfilePage.tsx';
import { SignInPage } from './screens/SignInPage.tsx';
import { StatsPage } from './screens/StatsPage.tsx';
import { About, NotFound, Privacy } from './screens/Static.tsx';
import { VariantPage } from './screens/VariantPage.tsx';
import { YouPage } from './screens/YouPage.tsx';

/** Screens that work without the server (games against the computer and pass & play). */
const OFFLINE_OK = new Set(['bot', 'local']);

function route(path: string) {
  const seg = path.split('/').filter(Boolean).map(decodeURIComponent);
  if (seg.length === 0) return { kind: 'home', node: <Home /> };
  const [a, b] = seg;
  if (seg.length === 1) {
    switch (a) {
      case 'leaderboard':
      case 'ranks':
        return { kind: 'leaderboard', node: <LeaderboardPage /> };
      case 'ideas':
        return { kind: 'ideas', node: <IdeasPage /> };
      case 'days':
        return { kind: 'days', node: <DaysPage /> };
      case 'you':
        return { kind: 'you', node: <YouPage /> };
      case 'stats':
        return { kind: 'stats', node: <StatsPage /> };
      case 'admin':
        return { kind: 'admin', node: <AdminPage /> };
      case 'challenge':
        return { kind: 'challenge', node: <ChallengeScreen />, game: true };
      case 'about':
        return { kind: 'about', node: <About /> };
      case 'privacy':
        return { kind: 'privacy', node: <Privacy /> };
      case 'signin':
        return { kind: 'signin', node: <SignInPage /> };
    }
    if (getVariant(a)) return { kind: 'variant', node: <VariantPage id={a} /> };
  }
  if (seg.length === 2) {
    if (a === 'game') return { kind: 'game', node: <OnlineGameScreen id={b} />, game: true };
    if (a === 'join') return { kind: 'join', node: <JoinPage code={b} /> };
    if (a === 'u') return { kind: 'profile', node: <ProfilePage username={b} /> };
    if (getVariant(a) && (b === 'bot' || b === 'local')) return { kind: b, node: <LocalGameScreen variantId={a} mode={b} />, game: true };
  }
  return { kind: 'notfound', node: <NotFound /> };
}

export function App() {
  const { path } = useLocation();
  const session = useSession();
  const online = useOnline();
  const ui = useUi();
  const r = route(path);

  useEffect(
    () =>
      client.onGameStart((id) => {
        sounds.start();
        navigate(`/game/${id}`);
      }),
    [],
  );

  useEffect(() => {
    if (online.error && Date.now() - online.error.at < 2000) {
      toast(online.error.message);
      client.clearError();
    }
  }, [online.error]);

  useEffect(() => {
    if (session.status === 'ready') track('view', { page: r.kind });
  }, [r.kind, session.status]);

  return (
    <div className={`app is-${r.kind} ${r.game ? 'in-game' : 'with-nav'} ${r.kind === 'admin' ? 'wide' : ''}`}>
      <ConnectionBanner />
      {session.error === 'offline' && !r.game && (
        <div className="banner" role="status">
          You’re offline. Games against the computer still work; online play comes back by itself.
        </div>
      )}
      {!r.game && <Header />}
      <main>
        {session.status === 'error' && !OFFLINE_OK.has(r.kind) ? (
          <div className="page narrow center">
            <h1>Can’t reach ChessBut</h1>
            <p className="muted">You can still play the computer while you’re offline.</p>
            <button className="btn primary big" onClick={() => navigate('/snail/bot')}>
              Play the computer
            </button>
            <button className="btn" onClick={() => location.reload()}>
              Try again
            </button>
          </div>
        ) : (
          r.node
        )}
      </main>
      {!r.game && <Footer />}
      {!r.game && <BottomNav />}
      <SearchingBar />
      <RoomPanel />
      <ShareFallback />
      {ui.auth && <AuthModal initial={ui.auth.mode} reason={ui.auth.reason} onClose={closeAuth} />}
      {ui.settings && <SettingsModal onClose={closeSettings} />}
      <Toasts />
    </div>
  );
}
